import React from 'react';
import SparkChatBubbleInner from 'components/common/Chats/SparkChatBubbleInner';

import { FileRejection } from 'react-dropzone/.';
import { useBlobStorage } from 'hooks/useBlobStorage';
import { useFileUploads } from 'hooks/useFileUploads';
import { Box } from '@mui/material';
import { useAddChatMessage } from 'hooks/chats/useAddChatMessage';
import { AttachmentCardType } from 'components/common/Chats/AttachmentCard';
import type { ChatMessageItem } from 'types/Chats/ChatMessage';
import { useWorkspaceStore } from 'stores';
import { v4 } from 'uuid';
type MemoryChatProps = {
  memoryID: string;
  chatID?: string;
};

function MemoryChat({ memoryID, chatID }: MemoryChatProps) {
  const [attachments, setAttachments] = React.useState<AttachmentCardType[]>(
    []
  );
  const { workspace } = useWorkspaceStore();
  const { handleFileUpload } = useBlobStorage(
    workspace?.attributes.containerName || 'app-assets'
  );
  const { mutateAsync: uploadFiles } = useFileUploads();
  const { mutateAsync: addMessage } = useAddChatMessage(chatID, 'memory');

  const handleMessageSend = async (message: string) => {
    const chatMessageID = v4();
    const defaultAttachment: ChatMessageItem = {
      type: 'memory',
      id: memoryID,
    };
    const items = attachments.map((item) => ({
      type: item.cardType,
      id: item.id,
    }));
    await addMessage({
      id: chatMessageID,
      content: message,
      chatID,
      memoryID,
      items: [defaultAttachment, ...items],
    });
  };

  const handleSuccessfulUpload = async (
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ) => {
    // Create initial attachments with loading state
    const mappedAcceptedFiles: AttachmentCardType[] = acceptedFiles.map(
      (file) => {
        const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';

        return {
          id: file.name, // Using temporary ID based on filename
          title: file.name,
          fileTypeTag: fileExtension,
          file: file,
          details: 'Document',
          isUploading: true,
          cardType: 'file',
          thumbnail: '',
          actionKey: file.name,
        };
      }
    );

    // Add new attachments with loading state
    setAttachments((prev) => [...prev, ...mappedAcceptedFiles]);

    try {
      // Upload to blob storage
      const result = await handleFileUpload(acceptedFiles, fileRejections);
      if (!result?.length) return;
      const URLs = result?.map((upload) => upload.url);
      const fileNameMap = result?.reduce<Record<string, string>>(
        (acc, upload) => {
          return { ...acc, [upload.file.name]: upload.fileName };
        },
        {}
      );
      // Register with fileUploads endpoint
      const uploadResponse = await uploadFiles({
        memoryID,
        files: URLs.map((fileUrl) => ({ url: fileUrl })),
      });
      const files = uploadResponse.data.attributes.files;

      // Update attachments with final data
      setAttachments((prev) => {
        return prev.map((attachment) => {
          // Find matching uploaded file
          const uploadedFile = files.find(
            (file) => file.fileName === fileNameMap[attachment.title]
          );

          if (uploadedFile) {
            return {
              ...attachment,
              id: uploadedFile.id || '',
              isUploading: false,
              url: uploadedFile.tempBLOBURL || uploadedFile.url,
            };
          }
          return attachment;
        });
      });

      return URLs;
    } catch (error) {
      // Handle error by updating failed attachments
      setAttachments((prev) =>
        prev.map((attachment) =>
          mappedAcceptedFiles.some((file) => file.id === attachment.id)
            ? { ...attachment, isUploading: false, error: true }
            : attachment
        )
      );
      console.error('Upload failed:', error);
    }
  };

  const handleAddAttachments = (attachments: AttachmentCardType) => {
    setAttachments((prev) => {
      // Check if attachment with same id already exists
      const exists = prev.some(
        (attachment) => attachment.id === attachments.id
      );
      if (exists) return prev;
      return [...prev, attachments];
    });
  };

  const handleAttachmentRemove = (
    cardType: AttachmentCardType['cardType'],
    id: AttachmentCardType['id']
  ) => {
    setAttachments((prev) => prev.filter((attachment) => attachment.id !== id));
  };
  return (
    <Box width='100%' justifyItems='center'>
      <SparkChatBubbleInner
        attachments={attachments}
        onSendMessage={handleMessageSend}
        onFileUpload={handleSuccessfulUpload}
        onAttachmentRemove={handleAttachmentRemove}
        openExpandedView={() => {}}
        onAddAttachments={handleAddAttachments}
        chatID={chatID}
      />
    </Box>
  );
}

export default MemoryChat;
